/**
 * Сроки ПРО для карточки на /pricing у иностранца на ру: цена, подпись в
 * окне и строка выгоды — в его валюте, из ответа /api/stripe/pro-prices
 * (useStripeProPrices). Цифры не подставляются вручную: суммы — ровно те,
 * что спишет Stripe; строка выгоды считается из них же.
 *
 * Рублёвая цена срока остаётся в rubPrice — её показывает только ссылка
 * «Оплатить … российской картой».
 *
 * Пока цены грузятся — «…» вместо суммы и без строки выгоды; запрос не
 * удался — сроки как были (рубли), оплата через окно при этом работает.
 */
import { formatStripeAmount } from '@/components/payment/StripePaymentModal';
import { CURRENCY_SYMBOLS } from '@/hooks/useCurrency';

export const TERM_PLAN = { month: 'PRO_MONTHLY', year: 'PRO_YEARLY', forever: 'FOUNDER' };

const fmt = (p) => formatStripeAmount(Number(p.amount), p.symbol || CURRENCY_SYMBOLS[p.currency] || p.currency);

/** Сумма одного плана из ответа /api/stripe/pro-prices: «9,99 $», «5 000 ₸». */
export const formatProPrice = fmt;

function monthsWord(n) {
  const tail = n % 10;
  const teen = n % 100 >= 11 && n % 100 <= 14;
  if (tail === 1 && !teen) return 'месяц';
  if (tail >= 2 && tail <= 4 && !teen) return 'месяца';
  return 'месяцев';
}

const round2 = (x) => Math.round(x * 100) / 100;

function benefitFor(key, plans) {
  const m = plans.PRO_MONTHLY;
  const p = plans[TERM_PLAN[key]];
  if (!m || !p || m.currency !== p.currency || !(Number(m.amount) > 0)) return null;
  if (key === 'year') {
    const twelve = round2(Number(m.amount) * 12);
    const save = round2(twelve - Number(p.amount));
    if (!(save > 0)) return null;
    return `Вместо ${fmt({ ...m, amount: twelve })} помесячно — выгода ${fmt({ ...m, amount: save })} за год`;
  }
  if (key === 'forever') {
    const n = Math.round(Number(p.amount) / Number(m.amount));
    if (!(n > 1)) return null;
    return `Разовый платёж: как ${n} ${monthsWord(n)} по ${fmt(m)}, дальше не списывается ничего`;
  }
  return null;
}

/**
 * Подпись главной кнопки ПРО у иностранца — с ценой в его валюте прямо на
 * кнопке, как у разовой покупки: «Оформить ПРО — 5 000 ₸». Пока цена
 * грузится («…») — без суммы.
 */
export function proPayButtonLabel(price) {
  const p = String(price || '').trim();
  return p && p !== '…' ? `Оформить ПРО — ${p}` : 'Оформить ПРО';
}

export function foreignProTerms(terms, prices) {
  if (!prices || prices.status === 'off' || prices.status === 'error') return terms;
  if (prices.status === 'loading' || !prices.plans) {
    return terms.map((t) => ({ ...t, rubPrice: t.price, price: '…', benefit: null }));
  }
  return terms.map((t) => {
    const p = prices.plans[TERM_PLAN[t.key]];
    if (!p) return { ...t, rubPrice: t.price };
    const price = fmt(p);
    return {
      ...t,
      rubPrice: t.price,
      price,
      payLabel: t.key === 'forever' ? `${price} навсегда` : `${price}${t.unit}`,
      benefit: benefitFor(t.key, prices.plans),
    };
  });
}
